import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoCreateOutline, IoStorefrontOutline, IoPersonOutline, IoCalendarOutline } from "react-icons/io5";
import api, { fileUrl } from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { AppHeader } from "@/components/AppHeader";
import { formatDateTimeIT } from "@/utils";

export default function ProblemDetailPage() {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/problems/${problemId}`);
        setP(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [problemId]);

  if (loading || !p) {
    return (
      <div className="mobile-frame" data-testid="problem-detail-page">
        <AppHeader title={t("problems")} showBack backTo="/problems" />
        <div className="tf-empty">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="mobile-frame" data-testid="problem-detail-page">
      <AppHeader title={t("problems")} showBack backTo="/problems" />
      <div className="tf-content tf-problem-detail">
        {/* Hero card */}
        <div className="tf-problem-hero">
          <div className="tf-problem-store" data-testid="pdet-store">
            <IoStorefrontOutline size={14} />
            <span>{t(STORE_LABELS[p.store_id])}</span>
          </div>

          <h1 className="tf-problem-title" data-testid="pdet-title">{p.title}</h1>

          <div className="tf-problem-badges">
            <span className={`tf-priority tf-priority-${p.priority}`} data-testid="pdet-priority">
              {t(`priority_${p.priority}`)}
            </span>
            <span className={`tf-status tf-status-${p.status}`} data-testid="pdet-status">
              {t(`status_${p.status}`)}
            </span>
          </div>

          {p.description && (
            <p className="tf-problem-description" data-testid="pdet-description">
              {p.description}
            </p>
          )}
        </div>

        {/* Meta info */}
        <div className="tf-problem-meta">
          <div className="tf-problem-meta-row">
            <IoPersonOutline size={16} />
            <span className="tf-problem-meta-label">{t("user")}</span>
            <span className="tf-problem-meta-value">{p.created_by || "—"}</span>
          </div>
          <div className="tf-problem-meta-row">
            <IoCalendarOutline size={16} />
            <span className="tf-problem-meta-label">{t("when")}</span>
            <span className="tf-problem-meta-value">{formatDateTimeIT(p.created_at)}</span>
          </div>
        </div>

        {/* Photos */}
        {(p.photo_paths || []).length > 0 && (
          <div className="tf-problem-photos">
            <div className="tf-photo-grid">
              {p.photo_paths.map((id, idx) => (
                <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer">
                  <img data-testid={`pdet-photo-${idx}`} src={fileUrl(id)} alt="" loading="lazy" decoding="async" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Edit action */}
        <button
          data-testid="pdet-edit"
          className="tf-btn-primary tf-problem-edit-btn"
          onClick={() => navigate(`/problem/${problemId}/edit`)}
        >
          <IoCreateOutline size={18} />
          {t("edit")}
        </button>
      </div>
    </div>
  );
}
